import { DatesSetArg, EventClickArg, EventHoveringArg, EventInput } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from "@fullcalendar/interaction";
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from "react-intl";
import { useParams } from 'react-router-dom';
import { Col, Row } from 'reactstrap';
import { AssistanceUtilities } from '../../AssistanceUtilities';
import { ScheduleUtilities } from '../../ScheduleUtilities';
import ButtonComponent from '../../components/ButtonComponent';
import { EditScheduleComponent } from '../../components/EditScheduleComponent';
import Layout from '../../components/Layout/Layout';
import PageTitle from "../../components/Layout/PageTitle";
import MultiSelectComponent from '../../components/MultiSelectComponent';
import { CreateSchedulerNavBar } from '../../components/Scheduler/CreateSchedulerNavBar';
import SelectComponent from '../../components/SelectComponent';
import { useAuth } from '../../context/AuthContext';
import Toast from '../../helpers/Toast';
import { Utilities } from '../../helpers/Utilities';
import { GetSchedulerListDTO } from '../../models/Scheduler/GetSchedulerListDTO';
import { ScheduleDetailsDTO } from '../../models/Scheduler/ScheduleDetailsDTO';
import { ScheduleSearchResultDTO } from '../../models/Scheduler/SchedulerListDTO';
import { SchedulerService } from '../../services/SchedulerService';
import { useSchedule } from '../../context/ScheduleContext';


function SchedulerIndex(props: any) {
  const { currentUser, isTechnician, technicianCode } = useAuth();
  const { assistanceId } = useParams<{ assistanceId: string }>();
  const intl = useIntl();

  const [events, setEvents] = useState<EventInput[]>([]);
  const schedulerService = new SchedulerService();
  const [dateRange, setDateRange] = useState<{ start: Date; end: Date } | null>(null);
  const calendarRef = useRef(null);
  const [calendarView, setCalendarView] = useState('timeGridWeek');

  const { isCreateFormVisible, setIsCreateFormVisible } = useSchedule();

  const [fetchDataAfterCreate, setFetchDataAfterCreate] = useState<boolean>(false);

  // Filtros de pesquisa
  const [technician, setTechnician] = useState<string | null>((isTechnician() && currentUser != null && technicianCode !== null) ? technicianCode : null);
  const [scheduleType, setScheduleType] = useState<string | null>(null);

  const [technicianOptions, setTechnicianOptions] = useState<any[]>([]);

  // Tabs Editar / Cancelar Agendamento
  const [isEditScheduleComponentOpen, setIsEditScheduleComponentOpen] = useState<boolean>(false);
  const [currentScheduleId, setCurrentScheduleId] = useState<number | null>(null);
  const [currentSchedule, setCurrentSchedule] = useState<ScheduleDetailsDTO | null>(null);

  const onNewTaskClick = () => {
    setIsEditScheduleComponentOpen(false); // fechar componente de edição para substiruir e nao ter ambos abertos
    setIsCreateFormVisible(true);
  };

  useEffect(() => {
    if (assistanceId !== undefined) {
      AssistanceUtilities.GetAssistance(assistanceId);
    }
  }, [assistanceId])

  useEffect(() => {
  
    const loadTechnicians = async () => {
      const technicians = await Utilities.GetTechnicians();
      setTechnicianOptions(technicians);
    };
    loadTechnicians();
  }, []); 

  useEffect(() => {
    if (dateRange) {
      fetchData();
    }
  }, [dateRange, technician, scheduleType, isEditScheduleComponentOpen, isCreateFormVisible, fetchDataAfterCreate]);

  // Como temos navegação para agendamentos, precisamos de um context / wrapper, em que temos a variavél : isCreateFormVisible
  // Após a primeira vez que o mesmo fica a true, a sair e entrar na página de agendamentos, aparece componente sempre ativo, e não queremos isso
  // Então serve para limpar o valor para false, quando componente é desmontado
  useEffect(() => {
    // Função de limpeza para redefinir isCreateFormVisible quando o componente é desmontado
    return () => {
      setIsCreateFormVisible(false);
    };
  }, [setIsCreateFormVisible]);


  const handleDatesSet = (arg: DatesSetArg) => {
    if (dateRange === null || arg.start.getTime() !== dateRange.start.getTime() || arg.end.getTime() !== dateRange.end.getTime()) {
      setDateRange({ start: arg.start, end: arg.end });
    }
  };

  const fetchData = async () => {
    var data: GetSchedulerListDTO = {
      dateStart: dateRange != null ? dateRange.start : new Date(),
      dateEnd: dateRange != null ? dateRange.end : new Date(),
      technicianId: technician,
      scheduleType: scheduleType
    };

    var response = await schedulerService.GetAll(data);
    if (response.success === false) {
      Toast.Show("error", response.message);
      return;
    }

    var event: EventInput[] = [];

    response.obj.forEach((element: ScheduleSearchResultDTO) => {

      const color = element?.color ?? '';

      event.push({
        id: element?.scheduleId?.toString() ?? undefined,
        title: element?.assistanceId ? (element?.assistanceDocument ?? undefined) : '',
        start: element.dateStart.toString().split(".")[0],
        end: element.dateEnd?.toString().split(".")[0],
        backgroundColor: color,
        borderColor: color,
        extendedProps: {
          eventColor: color,
          scheduleType: element?.scheduleType,
        },
      } as EventInput);
    });

    setEvents(event);
  };

  const HandleClick = async (arg: EventClickArg) => {
    setIsEditScheduleComponentOpen(false); // em caso de clicarmos noutro agendamento, para abrir info desse mesmo agendamento clicado
    setIsCreateFormVisible(false); // fechar componente de criação para substiruir e nao ter ambos abertos

    const response = await schedulerService.GetById(Number(arg.event.id));
    if (response.success === false || response.obj == null) {
      Toast.Show("error", response.message);
      return;
    }

    setCurrentScheduleId(response.obj.scheduleId);
    setCurrentSchedule(response.obj);
    setIsEditScheduleComponentOpen(true);
  };

  const handleEventMouseEnter = (arg: EventHoveringArg) => {
    arg.el.style.cursor = 'pointer';

    const scheduleType = arg.event.extendedProps.scheduleType;
    if (scheduleType) {
      arg.el.setAttribute('title', `Tipo de Agendamento: ${scheduleType}`);
    }
  };

  const handleEventMouseLeave = (arg: EventHoveringArg) => {
    arg.el.style.cursor = '';

    arg.el.removeAttribute('title');
  };

  // Para ecras mais pequenos, o time grid será diário
  useEffect(() => {
    function handleResize() {
      const newView = window.innerWidth < 768 ? 'timeGridDay' : 'timeGridWeek';
      if (calendarView !== newView) {
        setCalendarView(newView);
      }
    }

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [calendarView]);

  useEffect(() => {
    if (calendarRef.current) {
      (calendarRef.current as any).getApi().changeView(calendarView);
    }
  }, [calendarView]);


  // opções podem ficar aqui e fullcalendar consegue obter isto (pode ir para utilities etcetc)
  // neste momento foi vocacionado para cores
  const calendarOptions = {
    eventDisplay: 'block',
    eventContent: (arg: any) => {
      const { event } = arg;
      const color = event.extendedProps.eventColor;
      return (
        <div style={{ backgroundColor: color }}>
          <b>{event.title}</b>
        </div>
      );
    },
  };

  const onCloseEditComponents = () => {
    setIsEditScheduleComponentOpen(false); // em caso de clicarmos noutro agendamento, para abrir info desse mesmo agendamento clicado
    setIsCreateFormVisible(false);
  };
  const onFetchData = () => {
    setFetchDataAfterCreate(!fetchDataAfterCreate);
  };

  return (
    <Layout>
      <div className="pageContainer" style={{ display: "flex", flexWrap: "wrap", flexDirection: "column", flex: "initial", width: "100%", margin: "0 auto" }}>
        <div className="pageHeader">
          <PageTitle title={"Calendário"} allowGoBack={false} />
        </div>
        {assistanceId && (
          <div className="pageHeader">
            <PageTitle style={{ fontSize: "14px" }} title={`Assistência Selecionada : ${assistanceId}`} allowGoBack={false} />
          </div>
        )}
        <Row>
          <Col
            xl={2}
            lg={3}
            md={3}
            sm={12}
            xs={12}
            style={{ marginTop: "1em" }}
          >
            <ButtonComponent text={intl.formatMessage({ id: "SchedulerIndex.NewScheduler" })}
              onClick={onNewTaskClick} />
          </Col>
        </Row>

        <div className='pageBody'>
          <Row>
            <Col xl={7}>

              <Row>
                <MultiSelectComponent
                  xl={6}
                  lg={6}
                  md={6}
                  sm={12}
                  xs={12}
                  style={{ width: "100%" }}
                  label={"Técnico"}
                  items={technicianOptions.length > 0 ? technicianOptions : undefined} 
                  itemsLoader={Utilities.GetTechnicians} 
                  setSelectedString={setTechnician}
                  selectString={technician}
                />

                <SelectComponent
                  xl={3}
                  lg={3}
                  md={3}
                  sm={12}
                  xs={12}
                  style={{ width: "100%", height: "43px" }}
                  label={"Tipo Agendamento"}
                  selectedItemValue={scheduleType}
                  items={ScheduleUtilities.GetScheduleTypes(intl)}
                  onChange={(scheduleType: string) => {
                    setScheduleType(scheduleType);
                  }}
                />
              </Row>

              <div className="pageComponent" style={{ width: "100%", height: "50%" }}>
                <FullCalendar
                  ref={calendarRef}
                  plugins={[dayGridPlugin, interactionPlugin, timeGridPlugin]}
                  initialView={calendarView}
                  locale="pt-PT"
                  selectable={true}
                  selectMirror={true}
                  headerToolbar={{
                    start: 'today,prev,next',
                    center: 'title',
                    end: 'dayGridMonth,timeGridWeek,timeGridDay'
                  }}
                  allDaySlot={false}
                  droppable={true}
                  scrollTime={'08:00:00'}
                  slotMinTime={'08:00:00'} // isto define o horário mínimo como 08:00
                  slotMaxTime={'21:00:00'} // isto define o horário máximo como 21:00
                  scrollTimeReset={false}
                  eventOverlap={true}
                  slotEventOverlap={false}
                  datesSet={handleDatesSet}
                  eventClick={HandleClick}
                  eventMouseEnter={handleEventMouseEnter}
                  eventMouseLeave={handleEventMouseLeave}
                  editable
                  contentHeight="auto"
                  events={events}

                  {...calendarOptions} // adiciona as opções extras criadas para cores
                />
              </div>
            </Col>

            {isCreateFormVisible && (
              <Col xl={5}>
                <CreateSchedulerNavBar
                  onFetchData={onFetchData}
                />
              </Col>
            )}

            {isEditScheduleComponentOpen && (
              <Col xl={5}>
                <EditScheduleComponent
                  scheduleId={currentScheduleId}
                  currentSchedule={currentSchedule}
                  onClose={onCloseEditComponents}
                />
              </Col>
            )}

          </Row>
        </div>
      </div>
    </Layout>
  )
}

export default SchedulerIndex